import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

const TransactionContext = createContext();

export const useTransactions = () => {
  const context = useContext(TransactionContext);
  if (!context) {
    throw new Error('useTransactions must be used within a TransactionProvider');
  }
  return context;
};

export const TransactionProvider = ({ children }) => {
  const { token, isAuthenticated } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch transactions from backend when authenticated
  useEffect(() => {
    const fetchTransactions = async () => {
      if (!isAuthenticated || !token) {
        setTransactions([]);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const data = await api.getTransactions(token);
        // Transform backend data to match frontend format
        const formatted = data.map(t => ({
          ...t,
          id: t._id,
          _id: t._id,
        }));
        setTransactions(formatted);
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [token, isAuthenticated]);

  const addTransaction = async (transaction) => {
    try {
      const data = {
        description: transaction.description,
        category: transaction.category,
        amount: transaction.type === 'expense' ? -Math.abs(parseFloat(transaction.amount)) : Math.abs(parseFloat(transaction.amount)),
        type: transaction.type,
        date: transaction.date,
      };
      
      const newTransaction = await api.createTransaction(data, token);
      setTransactions(prev => [{...newTransaction, id: newTransaction._id}, ...prev]);
      return { success: true, transaction: newTransaction };
    } catch (err) {
      console.error('Failed to add transaction:', err);
      return { success: false, error: err.message };
    }
  };

  const updateTransaction = async (updatedTransaction) => {
    try {
      const data = {
        description: updatedTransaction.description,
        category: updatedTransaction.category,
        amount: updatedTransaction.type === 'expense' ? -Math.abs(parseFloat(updatedTransaction.amount)) : Math.abs(parseFloat(updatedTransaction.amount)),
        type: updatedTransaction.type,
        date: updatedTransaction.date,
      };
      
      const updated = await api.updateTransaction(updatedTransaction._id || updatedTransaction.id, data, token);
      setTransactions(prev => prev.map(t => (t._id === updated._id || t.id === updated._id) ? {...updated, id: updated._id} : t));
      return { success: true };
    } catch (err) {
      console.error('Failed to update transaction:', err);
      return { success: false, error: err.message };
    }
  };

  const deleteTransaction = async (id) => {
    try {
      await api.deleteTransaction(id, token);
      setTransactions(prev => prev.filter(t => t._id !== id && t.id !== id));
      return { success: true };
    } catch (err) {
      console.error('Failed to delete transaction:', err);
      return { success: false, error: err.message };
    }
  };

  const getStats = () => {
    const totalIncome = transactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0);
    const totalExpenses = transactions.filter(t => t.amount < 0).reduce((sum, t) => sum + Math.abs(t.amount), 0);
    const netBalance = totalIncome - totalExpenses;

    return {
      totalBalance: netBalance,
      monthlyIncome: totalIncome,
      monthlyExpenses: totalExpenses,
      savings: netBalance > 0 ? netBalance * 0.2 : 0,
      netBalance
    };
  };

  const value = {
    transactions,
    loading,
    error,
    addTransaction,
    updateTransaction,
    deleteTransaction,
    getStats
  };

  return (
    <TransactionContext.Provider value={value}>
      {children}
    </TransactionContext.Provider>
  );
};

export default TransactionContext;